import { createFileRoute, Navigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usersApi } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import { useAuthStore } from '@/store/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { PageHeader, TableShell } from '@/components/layout'
import { Plus, User, Edit, Trash2, Search, AlertCircle, CheckCircle2 } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'

export const Route = createFileRoute('/admin/users')({
  component: UsersPage,
})

// 飞书群聊 ID 校验：oc_ 开头，允许留空（留空 = 不接收机器人通知）
const larkChatIdSchema = z.union([
  z.literal(''),
  z.string().regex(/^oc_/, '飞书群聊 ID 需以 oc_ 开头'),
])

const createUserSchema = z.object({
  email: z.string().email('邮箱格式不正确'),
  name: z.string().min(1, '姓名不能为空'),
  role: z.enum(['founder', 'pm', 'translator']),
  languagePairs: z.array(z.string()).optional(),
  larkChatId: larkChatIdSchema.optional(),
})

const editUserSchema = z.object({
  name: z.string().min(1, '姓名不能为空'),
  role: z.enum(['founder', 'pm', 'translator']),
  languagePairs: z.array(z.string()).optional(),
  isActive: z.boolean(),
  larkChatId: larkChatIdSchema.optional(),
})

const LANGUAGE_PAIRS = ['zh-en', 'en-zh', 'zh-ja', 'ja-zh', 'zh-ko', 'ko-zh', 'en-ja', 'ja-en']

/** 用户管理页面：创始人/PM 管理用户与译者账号。 */
function UsersPage() {
  const { user } = useAuthStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<any>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)

  if (!user) {
    return <Navigate to="/login" />
  }

  // 译者不可见用户管理模块
  if (user.role === 'translator') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              访问受限
            </CardTitle>
            <CardDescription>
              译者用户无法访问用户管理页面
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              您的当前角色：<Badge variant="secondary">{user.role}</Badge>
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title="用户管理"
        description={user.role === 'founder' ? '管理系统用户和译者' : '管理译者用户'}
        actions={
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            添加用户
          </Button>
        }
      />

      <UserList
        searchQuery={searchQuery}
        roleFilter={roleFilter}
        onSearchChange={setSearchQuery}
        onRoleFilterChange={setRoleFilter}
        onEditClick={(user) => {
          setEditingUser(user)
          setEditDialogOpen(true)
        }}
      />

      <CreateUserDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {editingUser && (
        <EditUserDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          user={editingUser}
        />
      )}
    </div>
  )
}

/** 用户列表区：提供检索、筛选、编辑与停用等操作入口。 */
function UserList({
  searchQuery,
  roleFilter,
  onSearchChange,
  onRoleFilterChange,
  onEditClick,
}: {
  searchQuery: string
  roleFilter: string
  onSearchChange: (q: string) => void
  onRoleFilterChange: (r: string) => void
  onEditClick: (user: any) => void
}) {
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuthStore()
  const [userToDeactivate, setUserToDeactivate] = useState<any>(null)

  const { data: response, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getList(),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => {
      toast.success('用户已停用')
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (error: any) => {
      toast.error(error.error || '操作失败')
    },
  })

  const users = response?.data || []

  // PM用户前端也过滤一下，确保只显示译者
  const visibleUsers = currentUser?.role === 'pm' 
    ? users.filter((u: any) => u.role === 'translator')
    : users

  const filteredUsers = visibleUsers.filter((user: any) => {
    const matchesSearch = !searchQuery || 
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesRole = roleFilter === 'all' || user.role === roleFilter
    return matchesSearch && matchesRole
  })

  if (isLoading) {
    return <div className="py-8 text-center text-sm text-muted-foreground">加载中...</div>
  }

  return (
    <div className="space-y-4">
      <TableShell
        title="用户列表"
        toolbar={
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[260px] max-w-[420px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="搜索用户名或邮箱..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>
            {currentUser?.role === 'founder' && (
              <Select value={roleFilter} onValueChange={onRoleFilterChange}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="筛选角色" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部角色</SelectItem>
                  <SelectItem value="founder">创始人</SelectItem>
                  <SelectItem value="pm">项目经理</SelectItem>
                  <SelectItem value="translator">译者</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        }
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>用户</TableHead>
              <TableHead>邮箱</TableHead>
              <TableHead>角色</TableHead>
              <TableHead>语言对</TableHead>
              <TableHead>状态</TableHead>
              <TableHead>创建时间</TableHead>
              <TableHead className="text-right">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  没有找到匹配的用户
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user: any) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      {user.name}
                    </div>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge variant={user.role === 'founder' ? 'default' : 'secondary'}>
                      {user.role === 'founder' ? '创始人' : user.role === 'pm' ? '项目经理' : '译者'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.languagePairs?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {user.languagePairs.map((pair: string) => (
                          <Badge key={pair} variant="outline" className="text-xs">
                            {pair}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.isActive ? 'default' : 'secondary'}>
                      {user.isActive ? (
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          活跃
                        </span>
                      ) : (
                        '停用'
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(user.createdAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    {(currentUser?.role === 'founder' || user.role === 'translator') && (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => onEditClick(user)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {user.isActive && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setUserToDeactivate(user)}
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableShell>

      <Dialog open={!!userToDeactivate} onOpenChange={(open) => !open && setUserToDeactivate(null)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              确认停用用户
            </DialogTitle>
            <DialogDescription className="py-3">
              确定要停用 <span className="font-medium text-foreground">{userToDeactivate?.name}</span>（
              <span className="font-medium text-foreground">{userToDeactivate?.email}</span>）吗？
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setUserToDeactivate(null)}>
              取消
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!userToDeactivate) return
                if (import.meta.env.DEV) console.debug('[Users] 停用用户：', { id: userToDeactivate.id })
                deleteMutation.mutate(userToDeactivate.id)
                setUserToDeactivate(null)
              }}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? '处理中...' : '确认停用'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** 创建用户弹窗：校验表单并调用创建接口。 */
function CreateUserDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuthStore()
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'translator' as const,
    languagePairs: [] as string[],
    larkChatId: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})


  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => usersApi.create(data),
    onSuccess: () => {
      toast.success('用户创建成功')
      queryClient.invalidateQueries({ queryKey: ['users'] })
      onOpenChange(false)
      setFormData({ email: '', name: '', role: 'translator', languagePairs: [], larkChatId: '' })
      setErrors({})
    },
    onError: (error: any) => {
      toast.error(error.error || '创建失败')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const result = createUserSchema.safeParse(formData)
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      result.error.issues.forEach((issue) => {
        fieldErrors[issue.path[0]] = issue.message
      })
      setErrors(fieldErrors)
      return
    }
    setErrors({})
    if (import.meta.env.DEV) console.debug('[Users] 创建用户：', {
      role: formData.role,
      languagePairsCount: formData.languagePairs.length,
      hasLarkChatId: !!formData.larkChatId,
    })
    createMutation.mutate(formData)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>添加新用户</DialogTitle>
            <DialogDescription>
              {currentUser?.role === 'founder' 
                ? '创建新的系统用户账号' 
                : '创建新的译者账号'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">邮箱</Label>
              <Input
                id="email"
                placeholder="user@ptranslation.net"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">姓名</Label>
              <Input
                id="name"
                placeholder="用户姓名"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
            </div>
            {/* 只有创始人可以选择角色，PM只能创建译者 */}
            {currentUser?.role === 'founder' ? (
              <div className="space-y-2">
                <Label htmlFor="role">角色</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value: any) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="founder">创始人</SelectItem>
                    <SelectItem value="pm">项目经理</SelectItem>
                    <SelectItem value="translator">译者</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>角色</Label>
                <Input value="译者" disabled />
                <p className="text-sm text-muted-foreground">PM只能创建译者用户</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="larkChatId">飞书群 Chat ID（可选）</Label>
              <Input
                id="larkChatId"
                placeholder="oc_xxx"
                value={formData.larkChatId}
                onChange={(e) => setFormData({ ...formData, larkChatId: e.target.value.trim() })}
              />
              {errors.larkChatId && <p className="text-sm text-destructive">{errors.larkChatId}</p>}
              <p className="text-sm text-muted-foreground">
                任务派单、审批等机器人通知发送到的飞书群 ID，留空则不通知
              </p>
            </div>
            {formData.role === 'translator' && (
              <div className="space-y-2">
                <Label>语言对</Label>
                <div className="flex flex-wrap gap-2">
                  {LANGUAGE_PAIRS.map((pair) => (
                    <Badge
                      key={pair}
                      variant={formData.languagePairs.includes(pair) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          languagePairs: formData.languagePairs.includes(pair)
                            ? formData.languagePairs.filter((p: string) => p !== pair)
                            : [...formData.languagePairs, pair],
                        })
                      }}
                    >
                      {pair}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              取消
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? '创建中...' : '创建'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** 编辑用户弹窗：更新用户基础信息、角色与语言对。 */
function EditUserDialog({ open, onOpenChange, user }: { open: boolean; onOpenChange: (o: boolean) => void; user: any }) {
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuthStore()
  const [formData, setFormData] = useState({
    name: user.name,
    role: user.role,
    languagePairs: user.languagePairs || [],
    isActive: user.isActive,
    larkChatId: user.larkChatId || '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const updateMutation = useMutation({
    mutationFn: (data: typeof formData) => usersApi.update(user.id, data),
    onSuccess: () => {
      toast.success('用户信息已更新')
      queryClient.invalidateQueries({ queryKey: ['users'] })
      onOpenChange(false)
      setErrors({})
    },
    onError: (error: any) => {
      toast.error(error.error || '更新失败')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const result = editUserSchema.safeParse(formData)
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      result.error.issues.forEach((issue) => {
        fieldErrors[issue.path[0]] = issue.message
      })
      setErrors(fieldErrors)
      return
    }
    setErrors({})
    if (import.meta.env.DEV) console.debug('[Users] 更新用户：', {
      id: user.id,
      role: formData.role,
      isActive: formData.isActive,
      languagePairsCount: formData.languagePairs.length,
      hasLarkChatId: !!formData.larkChatId,
    })
    updateMutation.mutate(formData)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>编辑用户</DialogTitle>
            <DialogDescription>修改用户信息</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>邮箱</Label>
              <Input value={user.email} disabled />
              <p className="text-sm text-muted-foreground">邮箱不可修改</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">姓名</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
            </div>
            {/* 只有创始人可以修改角色，PM不能修改译者角色 */}
            {currentUser?.role === 'founder' ? (
              <div className="space-y-2">
                <Label htmlFor="role">角色</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value: any) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="founder">创始人</SelectItem>
                    <SelectItem value="pm">项目经理</SelectItem>
                    <SelectItem value="translator">译者</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>角色</Label>
                <Input value="译者" disabled />
                <p className="text-sm text-muted-foreground">PM不能修改用户角色</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="edit-larkChatId">飞书群 Chat ID</Label>
              <Input
                id="edit-larkChatId"
                placeholder="oc_xxx"
                value={formData.larkChatId}
                onChange={(e) => setFormData({ ...formData, larkChatId: e.target.value.trim() })}
              />
              {errors.larkChatId && <p className="text-sm text-destructive">{errors.larkChatId}</p>}
              <p className="text-sm text-muted-foreground">
                任务派单、审批等机器人通知发送到的飞书群 ID，留空则不通知
              </p>
            </div>
            {formData.role === 'translator' && (
              <div className="space-y-2">
                <Label>语言对</Label>
                <div className="flex flex-wrap gap-2">
                  {LANGUAGE_PAIRS.map((pair) => (
                    <Badge
                      key={pair}
                      variant={formData.languagePairs.includes(pair) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          languagePairs: formData.languagePairs.includes(pair)
                            ? formData.languagePairs.filter((p: string) => p !== pair)
                            : [...formData.languagePairs, pair],
                        })
                      }}
                    >
                      {pair}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="status">状态</Label>
              <Select
                value={formData.isActive ? 'active' : 'inactive'}
                onValueChange={(value) => setFormData({ ...formData, isActive: value === 'active' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">活跃</SelectItem>
                  <SelectItem value="inactive">停用</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              取消
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? '保存中...' : '保存'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
